import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Box,
  Grid,
  FormControl,
  Select,
  InputAdornment,
} from '@mui/material';
import PriceField from './PriceField';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

function AddSolution({ open, onClose, onAdd, editMode = false, solutionToEdit = {}, setSuccess, setError }) {
  const { user, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const defaultCurrency = farmData?.settings?.currency || '\u00A3';
  const defaultUnit = 'ml';
  const inlineSelectSx = {
    fontSize: '0.85rem',
    opacity: 0.85,
    '&:hover': { opacity: 1 },
    '&.Mui-focused': { opacity: 1 },
    '& .MuiSelect-icon': { fontSize: 18 },
  };

  const renderInlineSelect = (value, onChange, options) => (
    <Select
      variant="standard"
      disableUnderline
      sx={inlineSelectSx}
      value={value}
      onChange={onChange}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const [newSolution, setNewSolution] = useState({
    name: '',
    packetVolume: '',
    volumeUnit: defaultUnit,
    packetCost: '',
    costCurrency: defaultCurrency,
    notes: '',
  });

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editMode && solutionToEdit && solutionToEdit.id) {
      setNewSolution({
        name: solutionToEdit.name || '',
        packetVolume: solutionToEdit.packetVolume || solutionToEdit.packetWeight || '',
        volumeUnit: solutionToEdit.volumeUnit || solutionToEdit.weightUnit || defaultUnit,
        packetCost: solutionToEdit.packetCost || '',
        costCurrency: solutionToEdit.costCurrency || defaultCurrency,
        notes: solutionToEdit.notes || '',
      });
    } else {
      setNewSolution({
        name: '',
        packetVolume: '',
        volumeUnit: defaultUnit,
        packetCost: '',
        costCurrency: defaultCurrency,
        notes: '',
      });
    }

    setFormSubmitted(false);
    setTouched({});
  }, [open, editMode, solutionToEdit, defaultCurrency, defaultUnit]);

  const handleSave = async () => {
    setFormSubmitted(true);
    if (!newSolution.name) {
      setError?.('Please fill in required field: Name');
      return;
    }
    try {
      if (!user?.uid) {
        setError?.('User not logged in.');
        return;
      }
      if (!activeFarmId) {
        setError?.('No active farm selected.');
        return;
      }

      if (editMode && solutionToEdit?.id) {
        await updateDoc(doc(db, 'farms', activeFarmId, 'solutions', solutionToEdit.id), newSolution);
        setSuccess?.('Solution updated successfully.');
      } else {
        await addDoc(collection(db, 'farms', activeFarmId, 'solutions'), newSolution);
        setSuccess?.('Solution added successfully.');
      }
      onAdd?.(newSolution);
      handleCloseDialog();
    } catch (error) {
      setError?.('Error saving solution.');
    }
  };

  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const isError = (field) => {
    return !newSolution[field] && (touched[field] || formSubmitted);
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
      <DialogTitle>{editMode ? 'Edit Solution' : 'Add New Solution'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              label="Name"
              fullWidth
              required
              value={newSolution.name}
              onChange={(e) => setNewSolution({ ...newSolution, name: e.target.value })}
              onBlur={() => handleBlur('name')}
              error={isError('name')}
              helperText={isError('name') ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Packet Volume"
              fullWidth
              value={newSolution.packetVolume}
              onChange={(e) => setNewSolution({ ...newSolution, packetVolume: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {renderInlineSelect(
                      newSolution.volumeUnit,
                      (e) => setNewSolution({ ...newSolution, volumeUnit: e.target.value }),
                      [
                        { value: 'ml', label: 'ml' },
                        { value: 'l', label: 'l' },
                        { value: 'fl oz', label: 'fl oz' },
                        { value: 'gal', label: 'gal' },
                      ]
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PriceField
              label="Packet Cost"
              fullWidth
              value={newSolution.packetCost}
              onChange={(formattedValue) => setNewSolution({ ...newSolution, packetCost: formattedValue })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {renderInlineSelect(
                      newSolution.costCurrency,
                      (e) => setNewSolution({ ...newSolution, costCurrency: e.target.value }),
                      [
                        { value: '\u00A3', label: '\u00A3' },
                        { value: '$', label: '$' },
                        { value: '\u20AC', label: '\u20AC' },
                      ]
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <TextField
          margin="dense"
          label="Notes"
          fullWidth
          multiline
          minRows={2}
          value={newSolution.notes}
          onChange={(e) => setNewSolution({ ...newSolution, notes: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          {editMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSolution;



